import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import cartService from '../services/cartService';
import wishlistService from '../services/wishlistService';
import { priceInfo } from '../utils/priceUtils';
import { useCurrency } from '../context/CurrencyContext';
import { useCompare } from '../context/CompareContext';
import { useStickers } from '../context/StickerContext';
import { useLoanCampaign } from '../context/LoanContext';
import ProductSticker from './ProductSticker';
import ProductLoanBadge from './ProductLoanBadge';
import toast from 'react-hot-toast';

export default function ProductListCard({ product }) {
  const { formatPrice } = useCurrency();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();
  const { getStickersForProduct } = useStickers();
  const { getLoanForProduct } = useLoanCampaign();
  const matchingStickers = getStickersForProduct ? getStickersForProduct(product) : [];
  const loanInfo = getLoanForProduct ? getLoanForProduct(product) : null;
  const navigate = useNavigate();
  const [inWishlist, setInWishlist] = useState(false);
  const [inCart, setInCart] = useState(false);
  const isOutOfStock = !product || product.stock == null || Number(product.stock) <= 0;

  const info = priceInfo(product);
  const imgSrc = (product.images && product.images.length) ? product.images[0] : product.image;

  useEffect(() => {
    let mounted = true;
    const checkWishlist = async () => {
      const list = await wishlistService.getWishlist();
      if (!mounted) return;
      setInWishlist(list.some(i => String(i.id) === String(product.id)));
    };
    const checkCart = async () => {
      const items = await cartService.getCart();
      if (!mounted) return;
      setInCart(items.some(i => String(i.id) === String(product.id)));
    };
    checkWishlist();
    checkCart();

    const handleWishlistUpdate = (e) => {
      const list = e.detail?.items || [];
      if (!mounted) return;
      setInWishlist(list.some(i => String(i.id) === String(product.id)));
    };
    const handleCartUpdate = (e) => {
      const list = e.detail?.items || [];
      if (!mounted) return;
      setInCart(list.some(i => String(i.id) === String(product.id)));
    };

    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', checkCart);
    return () => {
      mounted = false;
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', checkCart);
    };
  }, [product.id]);

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await wishlistService.toggleItem(product);
      toast.success(inWishlist ? 'U hoq nga lista e dëshirave' : 'U shtua në listën e dëshirave');
    } catch (err) {
      toast.error('Dështoi përditësimi i listës së dëshirave');
    }
  };

  const handleCartToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) {
      toast.error('Out of stock');
      return;
    }
    const nextState = !inCart;
    setInCart(nextState);
    try {
      if (!nextState) {
        await cartService.removeItem(product.id);
        toast.success('U hoq nga shporta');
      } else {
        await cartService.addItem({
          id: product.id,
          name: product.name,
          price: info.display,
          image: product.image,
          sku: product.sku
        }, 1);
        toast.success('U shtua në shportë');
      }
    } catch (err) {
      setInCart(!nextState);
      toast.error(!nextState ? 'Dështoi heqja nga shporta' : 'Dështoi shtimi në shportë');
    }
  };

  const handleBuyNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) {
      toast.error('Out of stock');
      return;
    }
    try {
      await cartService.addItem({
        id: product.id,
        name: product.name,
        price: info.display,
        image: product.image,
        sku: product.sku
      }, 1);
      navigate('/cart');
    } catch (err) {
      toast.error('Dështoi shtimi në shportë');
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #eee',
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        height: '220px',
        maxHeight: '220px',
        boxSizing: 'border-box',
        transition: 'box-shadow 0.2s, transform 0.2s',
        position: 'relative'
      }}
      className={'product-card-hover products-card list'}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Custom Campaign Stickers */}
      {matchingStickers.map(stk => (
        <ProductSticker key={stk.id} sticker={stk} />
      ))}

      {/* Custom Loan Campaign Badge */}
      {loanInfo && (
        <ProductLoanBadge loanInfo={loanInfo} placement="card" />
      )}

      {/* Badge */}
      {info.discounted && (
        <div className="product-badge" style={{ position: 'absolute', top: 10, left: 10 }}>
          -{Math.round((1 - info.display / info.original) * 100)}%
        </div>
      )}



      {/* Compare Button */}
      <button
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
        }}
        className={`compare-btn ${isInCompare(product.id) ? 'active' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          isInCompare(product.id) ? removeFromCompare(product.id) : addToCompare(product);
        }}
        title={isInCompare(product.id) ? "Hiq nga Krahasimi" : "Shto në Krahasim"}
        aria-label={isInCompare(product.id) ? "Hiq nga Krahasimi" : "Shto në Krahasim"}
      >
        <svg
          viewBox="0 0 122.88 102.72"
          className="icon-svg"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M65.61,20.91v72.74h35.63c0.38,0,0.68,0.31,0.68,0.69v7.7c0,0.38-0.31,0.69-0.68,0.69H22.84c-0.38,0-0.69-0.31-0.69-0.69v-7.7c0-0.38,0.31-0.69,0.69-0.69h35.63l0-72.71c-3.1-1.08-5.56-3.53-6.64-6.63H29.3v3.43c0,0.38-0.31,0.68-0.68,0.68h-5.78c-0.38,0-0.69-0.31-0.69-0.68v-3.43h-5.73c-0.44,0-0.8-0.31-0.8-0.68V7.84c0-0.38,0.36-0.69,0.8-0.69h35.43C53.33,2.99,57.31,0,61.99,0c4.68,0,8.66,2.99,10.14,7.16h35.53c0.44,0,0.8,0.31,0.8,0.69v5.78c0,0.38-0.36,0.68-0.8,0.68h-6.46v3.43c0,0.38-0.31,0.68-0.68,0.68h-5.78c-0.38,0-0.69-0.31-0.69-0.68v-3.43H72.16C71.09,17.38,68.67,19.81,65.61,20.91ZM99.66,22.3l22.91,40.48c0.2,0.35,0.29,0.73,0.28,1.1h0.02v0.15c0,9.64-11.35,17.46-25.35,17.46c-13.85,0-25.1-7.65-25.34-17.15c-0.04-0.16-0.06-0.34-0.06-0.51c0-0.44,0.14-0.86,0.37-1.2L95.92,22.2c0.59-1.02,1.89-1.37,2.91-0.78C99.2,21.65,99.48,21.95,99.66,22.3ZM99.75,31.11v30.6h17.32L99.75,31.11ZM95.67,61.7V31.16L77.96,61.7H95.67ZM27.54,22.3l22.91,40.48c0.2,0.35,0.29,0.73,0.28,1.1h0.02v0.15c0,9.64-11.35,17.46-25.35,17.46c-13.85,0-25.1-7.65-25.34-17.15C0.02,64.19,0,64.02,0,63.84c0-0.44,0.14-0.86,0.37-1.2L23.8,22.21c0.59-1.02,1.89-1.37,2.91-0.78C27.08,21.65,27.36,21.95,27.54,22.3ZM27.63,31.11v30.6h17.32L27.63,31.11ZM23.54,61.7V31.16L5.84,61.7H23.54ZM61.99,6.07c2.59,0,4.69,2.1,4.69,4.69c0,2.59-2.1,4.69-4.69,4.69c-2.59,0-4.69-2.1-4.69-4.69C57.3,8.17,59.4,6.07,61.99,6.07Z"
          />
        </svg>
      </button>

      <div className="products-card-image list-card-image" style={{
        width: '240px',
        minWidth: '240px',
        maxWidth: '240px',
        height: '100%',
        maxHeight: '100%',
        background: '#f9f9f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        boxSizing: 'border-box',
        flexShrink: 0,
        overflow: 'hidden'
      }}>
        {imgSrc ? (
          <Link to={`/products/${product.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <img 
              src={imgSrc} 
              alt={product.name} 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain' 
              }} 
            />
          </Link>
        ) : (
          <div style={{ color: '#ccc' }}>Pa imazh</div>
        )}
      </div>

      <div style={{ padding: '14px 18px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: '#888', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {product.brand || product.category || 'Generic'}
        </div>
        <Link to={`/products/${product.id}`} style={{
          textDecoration: 'none',
          color: '#333',
          fontWeight: 700,
          fontSize: 15.5,
          marginBottom: 6,
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {product.name}
        </Link>

        {product.description && product.description.trim().toLowerCase() !== product.name?.trim().toLowerCase() && (
          <div style={{
            fontSize: 13,
            color: '#666',
            marginBottom: 8,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {product.description}
          </div>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {info.discounted ? <span style={{ textDecoration: 'line-through', color: '#999', fontSize: 13, letterSpacing: '-0.5px' }}>{formatPrice(info.original)}</span> : ''}
            <span style={{ fontSize: '2rem', fontWeight: 500, color: '#007da5', letterSpacing: '-1px' }}>
              {formatPrice(info.display)}
            </span>
            {/* Loan Installment Subprice Text */}
            {loanInfo && loanInfo.card?.enabled && (loanInfo.card.displayMode === 'subprice' || loanInfo.card.displayMode === 'both') && (
              <div style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: '#007da5',
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                <span style={{ fontSize: 11 }}>💳</span>
                <span>{loanInfo.card.subpriceText}</span>
              </div>
            )}
            {isOutOfStock ? (
              <span style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#111827' }}>Out of stock</span>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className={`action-icon-btn wishlist-btn ${inWishlist ? 'active' : ''}`}
              onClick={handleWishlistToggle}
              title={inWishlist ? "Hiq nga dëshirat" : "Shto në dëshira"}
              aria-label={inWishlist ? "Hiq nga dëshirat" : "Shto në dëshira"}
            >
              <svg viewBox="0 0 24 24" fill={inWishlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>
            </button>
            <button
              className={`action-icon-btn cart-btn ${inCart ? 'active' : ''}`}
              onClick={handleCartToggle}
              title={inCart ? "Hiq nga shporta" : "Shto në shportë"}
              aria-label={inCart ? "Hiq nga shporta" : "Shto në shportë"}
              disabled={isOutOfStock}
              style={isOutOfStock ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            >
              <svg viewBox="0 0 24 24" fill={inCart ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
                <circle cx="8" cy="21" r="1" />
                <circle cx="19" cy="21" r="1" />
                <path d="M2.05 2.05h1.5l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
              </svg>
            </button>
            <button
              className="buy-now-btn"
              onClick={handleBuyNow}
              disabled={isOutOfStock}
              style={{
                padding: '0 20px',
                flex: 'none',
                ...(isOutOfStock && {
                  opacity: 0.6,
                  cursor: 'not-allowed'
                })
              }}
              aria-label={isOutOfStock ? 'Jashtë stokut' : 'Bli tani'}
            >
              {isOutOfStock ? 'JASHTË STOKUT' : 'BLI TANI'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
